import alea from 'alea'

const DEFAULT_ANGLE = 30
const MAX_SEGMENTS = 900
const MAX_INSTRUCTIONS = 8000
const ROOT_BRANCH_KEY = 'root'

const baseProfiles = {
  branching: {
    name: 'branching',
    axiom: 'X',
    rules: {
      X: 'F[+X]F[-X]+X',
      F: 'FF',
    },
    iterationsMin: 2,
    iterationsMax: 5,
    angleDeg: 30,
    segmentLength: 1,
    thicknessRange: [1, 0.4],
  },
  vine: {
    name: 'willow',
    axiom: 'X',
    rules: {
      X: 'F[+X][-X]FX',
      F: 'FF',
    },
    iterationsMin: 2,
    iterationsMax: 5,
    angleDeg: 18,
    segmentLength: 0.9,
    thicknessRange: [1.1, 0.3],
    startAngleDeg: -80,
  },
  spiral: {
    name: 'spiral',
    axiom: 'F+XF+F+XF',
    rules: {
      X: 'XF-F+F-XF+F+XF-F+F-X',
    },
    iterationsMin: 2,
    iterationsMax: 4,
    angleDeg: 30,
    segmentLength: 1,
    thicknessRange: [1, 0.5],
  },
  fern: {
    name: 'fern',
    axiom: 'X',
    rules: {
      X: 'F-[[X]+X]+F[+FX]-X',
      F: 'FF',
    },
    iterationsMin: 2,
    iterationsMax: 4,
    angleDeg: 25,
    segmentLength: 0.95,
    thicknessRange: [0.9, 0.3],
  },
  bush: {
    name: 'bush',
    axiom: 'F',
    rules: {
      F: 'F[+F]F[-F]F',
    },
    iterationsMin: 2,
    iterationsMax: 5,
    angleDeg: 22,
    segmentLength: 0.85,
    thicknessRange: [1.1, 0.35],
  },
  zigzag: {
    name: 'zigzag',
    axiom: 'F',
    rules: {
      F: 'F+F-F-FF+F+F-F',
    },
    iterationsMin: 1,
    iterationsMax: 3,
    angleDeg: 40,
    segmentLength: 1,
    thicknessRange: [0.8, 0.4],
  },
  droop: {
    name: 'droop',
    axiom: 'X',
    rules: {
      X: 'F[+X][X][-X]FX',
      F: 'F[-F]F',
    },
    iterationsMin: 2,
    iterationsMax: 5,
    angleDeg: 28,
    segmentLength: 1.05,
    thicknessRange: [1, 0.45],
  },
}

const AVAILABLE_PROFILES = Object.values(baseProfiles)
const PROFILES_PER_PLANT = 3

const segmentCache = new Map()

const align = (value, precision = 0.01) => Math.round(value / precision) * precision

function sampleProfiles(rng, count = PROFILES_PER_PLANT) {
  const chosenIndexes = new Set()
  const pool = []
  const maxSamples = Math.min(count, AVAILABLE_PROFILES.length)

  while (chosenIndexes.size < maxSamples) {
    const index = Math.floor(rng() * AVAILABLE_PROFILES.length)
    if (chosenIndexes.has(index)) continue
    chosenIndexes.add(index)
    const profileTemplate = AVAILABLE_PROFILES[index]
    pool.push({ ...profileTemplate })
  }

  return pool
}

function pickProfile(plantConfig, seed) {
  const rng = alea(seed || plantConfig?.id || 'plant')
  const profilePool = sampleProfiles(rng)
  const index = Math.floor(rng() * profilePool.length) % profilePool.length
  return profilePool[Math.max(0, index)]
}

function buildSequence(profile, iterations) {
  let current = profile.axiom || 'F'
  for (let i = 0; i < iterations; i++) {
    let next = ''
    for (const char of current) {
      next += profile.rules[char] || char
      if (next.length > MAX_INSTRUCTIONS) {
        break
      }
    }
    current = next
    if (current.length > MAX_INSTRUCTIONS) {
      current = current.slice(0, MAX_INSTRUCTIONS)
      break
    }
  }
  return current
}

function turtleToSegments(sequence, profile, prunedBranches = [], rng = Math.random) {
  const angle = ((profile.angleDeg || DEFAULT_ANGLE) * Math.PI) / 180
  let direction = profile.startAngleDeg != null
    ? (profile.startAngleDeg * Math.PI) / 180
    : -Math.PI / 2
  const stateStack = []
  let x = 0
  let y = 0
  const segments = []
  let currentBranchKey = ROOT_BRANCH_KEY
  let branchCounter = 0
  const prunedKeys = (prunedBranches || []).filter(Boolean)

  for (const char of sequence) {
    if (segments.length >= (profile.maxSegments || MAX_SEGMENTS)) {
      break
    }

    switch (char) {
      case 'F':
      case 'G': {
        const step = profile.segmentLength || 1
        const nextX = x + Math.cos(direction) * step
        const nextY = y + Math.sin(direction) * step
        const alignedStart = { x: align(x), y: align(y) }
        const alignedEnd = { x: align(nextX), y: align(nextY) }

        if (!(alignedStart.x === alignedEnd.x && alignedStart.y === alignedEnd.y)) {
          segments.push({
            x1: alignedStart.x,
            y1: alignedStart.y,
            x2: alignedEnd.x,
            y2: alignedEnd.y,
            depth: stateStack.length,
            branchKey: currentBranchKey,
          })
        }

        x = nextX
        y = nextY
        break
      }
      case '+': {
        const randomSign = rng() < 0.5 ? 1 : -1
        direction += angle * randomSign
        break
      }
      case '-': {
        const randomSign = rng() < 0.5 ? 1 : -1
        direction -= angle * randomSign
        break
      }
      case '[':
        branchCounter += 1
        stateStack.push({ x, y, direction, branchKey: currentBranchKey })
        currentBranchKey = `${currentBranchKey}:${branchCounter}`
        break
      case ']': {
        const state = stateStack.pop()
        if (state) {
          x = state.x
          y = state.y
          direction = state.direction
          currentBranchKey = state.branchKey || ROOT_BRANCH_KEY
        } else {
          currentBranchKey = ROOT_BRANCH_KEY
        }
        break
      }
      default:
        // ignore other symbols
        break
    }
  }

  const total = segments.length
  const filteredSegments = prunedKeys.length === 0
    ? segments
    : segments.filter((segment) => {
        return !prunedKeys.some((key) => segment.branchKey === key || segment.branchKey.startsWith(`${key}:`))
      })

  return filteredSegments.map((segment, index) => {
    const progressRatio = total <= 1 ? 0 : index / (total - 1)
    const depthPenalty = segment.depth * 0.12
    const falloff = profile.thicknessRange || [1, 0.4]
    const thicknessFactor = Math.max(
      falloff[1] || 0.3,
      (falloff[0] || 1) - progressRatio * (falloff[0] - (falloff[1] || 0.3)) - depthPenalty,
    )
    return {
      ...segment,
      progressRatio,
      thicknessFactor,
    }
  })
}

export function getPlantSegments(plant, progress = 0, options = {}) {
  if (!plant || !plant.config) {
    return []
  }
  const plantConfig = plant.config
  const totalClicks = (plantConfig.clicksPerStage || 10) * (plantConfig.stages || 10)
  const maturity = totalClicks > 0 ? Math.min(1, Math.max(0, progress / totalClicks)) : 1
  const seed = plant.seed || plant.id || plant.plantedAt || 'plant'
  const profileSeed = `${seed}:profile`
  const turnSeed = `${seed}:turns`
  const prunedSignature = (plant.prunedBranches || []).filter(Boolean).sort().join('|')
  const cacheKey = `${plant.id || seed}:${progress}:${options.pixelSize || 'default'}:${prunedSignature}`

  if (segmentCache.has(cacheKey)) {
    return segmentCache.get(cacheKey)
  }

  const profile = pickProfile(plantConfig, profileSeed)
  const iterationsRange = profile.iterationsMax - profile.iterationsMin
  const iterations = Math.max(
    profile.iterationsMin,
    Math.min(
      profile.iterationsMax,
      Math.round(profile.iterationsMin + maturity * (iterationsRange || 0)),
    ),
  )

  const sequence = buildSequence(profile, iterations)
  const turnRng = alea(turnSeed)
  const segments = turtleToSegments(sequence, profile, plant.prunedBranches, turnRng)
  const maturityFactor = maturity <= 0 ? 0 : Math.max(0.1, maturity)
  const maturitySegments = maturityFactor === 0 ? 0 : Math.max(1, Math.round(segments.length * maturityFactor))
  const limitedSegments = maturitySegments === 0
    ? []
    : segments.slice(0, Math.min(segments.length, maturitySegments))

  const heightMultiplier = plantConfig.heightMultiplier || 1
  const scaledSegments = limitedSegments.map((segment) => ({
    ...segment,
    x1: segment.x1 * heightMultiplier,
    y1: segment.y1 * heightMultiplier,
    x2: segment.x2 * heightMultiplier,
    y2: segment.y2 * heightMultiplier,
  }))

  segmentCache.set(cacheKey, scaledSegments)
  return scaledSegments
}

export function clearSegmentCacheForPlant(plantId) {
  if (!plantId) return
  for (const key of segmentCache.keys()) {
    if (key.startsWith(`${plantId}:`)) {
      segmentCache.delete(key)
    }
  }
}
